import {
  DELIVERY_TOOLS, hasReviewGroup, isReviewGroupLead, LineItem,
} from '@printdeal/business-logic';
import splitOnFirst from 'split-on-first';
import { DeliveryToolSettings } from '@printdeal/product-selector';
import { IDescriptionItem } from '../components/my-account/components/LineItemDescription';
import { QuoteProductType } from '../types/quotes/quoteLineItems';
import type { SelectedDeliveryTool } from '../types/file-delivery/FileDelivery';

const stripSiblingsFromLineItems = (lineItems: LineItem[]): LineItem[] => lineItems.filter(
  (lineItem) => !hasReviewGroup(lineItem) || isReviewGroupLead(lineItem),
);

const splitAttributeAndValues = (localizedDescription: string): IDescriptionItem[] => localizedDescription.split(', ').reduce((acc, attribute) => {
  const [attributeName, attributeValue] = splitOnFirst(attribute, ': ');
  if (!attributeName || !attributeValue) {
    return acc;
  }
  acc.push({
    attributeName,
    attributeValue,
  });
  return acc;
}, [] as IDescriptionItem[]);

const splitQuoteLineItems = (lineItems: LineItem[]): {
  quoteLineItems: Record<string, string[]>,
  productLineItems: LineItem[],
} => {
  const quoteLineItems = {} as Record<string, string[]>;
  const productLineItems = [] as LineItem[];

  lineItems.forEach((lineItem) => {
    if (lineItem.custom?.fields?.quoteId
      && (lineItem.custom?.fields?.quoteProductType === 'hybrid'
      || lineItem.custom?.fields?.quoteProductType === 'bespoke')) {
      if (!quoteLineItems[lineItem.custom?.fields?.quoteId]) {
        quoteLineItems[lineItem.custom?.fields?.quoteId] = [];
      }
      quoteLineItems[lineItem.custom?.fields?.quoteId].push(
        lineItem.custom?.fields?.quoteLineItemId,
      );
    } else {
      productLineItems.push(lineItem);
    }
  });

  return { quoteLineItems, productLineItems };
};

const getActualEditorType = (editorType: string, deliveryToolSettings: DeliveryToolSettings): string => {
  const editorTypes = ['editor', 'printqeditor', 'designandprint', undefined];
  if ((editorTypes.includes(editorType)) && deliveryToolSettings?.editor === 'on' && deliveryToolSettings?.editormode) {
    return deliveryToolSettings.editormode;
  }
  // "Checkmatemode: checkmate" and "checkmate: on" will be renamed when MRC is replaced by the business rules app
  if (deliveryToolSettings?.checkmatemode) {
    return DELIVERY_TOOLS.UPLOADER_TOOL;
  }

  return DELIVERY_TOOLS.UPLOADER_TOOL;
};

const getSelectedDeliveryTool = (
  editorType: string, deliveryToolSettings: DeliveryToolSettings,
): SelectedDeliveryTool => {
  if (deliveryToolSettings?.ncolorcount) {
    return 'editor';
  }
  // "Checkmatemode: checkmate" and "checkmate: on" will be renamed when MRC is replaced by the business rules app
  if (deliveryToolSettings?.editor === 'on' && (!deliveryToolSettings?.checkmate || deliveryToolSettings.checkmate === 'off')) {
    return 'editor';
  }
  if (editorType === DELIVERY_TOOLS.UPLOADER_TOOL || editorType === DELIVERY_TOOLS.CHECKMATE || !editorType) {
    return DELIVERY_TOOLS.UPLOADER_TOOL as SelectedDeliveryTool;
  }
  return 'editor';
};

const isQuoteOfType = (
  lineItem: LineItem,
  quoteType: QuoteProductType,
): boolean => lineItem?.custom?.fields?.quoteProductType === quoteType;

const getFileDeliveryItemId = (lineItem: LineItem): string => {
  const { artworkItemId } = lineItem.custom?.fields || {};
  return artworkItemId || lineItem.id;
};

export {
  isReviewGroupLead,
  hasReviewGroup,
  stripSiblingsFromLineItems,
  splitAttributeAndValues,
  splitQuoteLineItems,
  getActualEditorType,
  getSelectedDeliveryTool,
  isQuoteOfType,
  getFileDeliveryItemId,
};
